import * as React from "react";
import { IMobileOffer } from "@nju/data-access-portal-offers";
import type { IMultiOrderOrchestrator } from "@nju/data-access-portal-orchestrator";
import { Icon } from "@nju/icons";
import { BlockText, Separator, Text } from "@nju/ui";
import { formatPhoneNumber, formatPrice, POSITIVE_DIGITS } from "@nju/utils";

interface ISidebarMobileOfferDuoTrioNumberProps {
  currentOnboardingMember: number | undefined;
  orderItems: IMultiOrderOrchestrator["multiOrderItems"];
  isOrderSummary: boolean;
  isPostpaid: boolean;
  discountFeatures: IMobileOffer["discountFeatures"] | undefined;
}

const mapProcessIdToText: {
  [K in Required<IMultiOrderOrchestrator>["processId"]]: string;
} = {
  MNP_IN: "przenoszony numer",
  NEW_NUMBER: "nowy numer",
  LEGACY_MIGRATION: "zmiana oferty",
} as const;

const mapDeliveryMethodIdToText: {
  [K in Required<IMultiOrderOrchestrator>["deliveryMethodId"]]: string;
} = {
  BLISTER: "",
  COURIER: "SIM - ",
  ESIM: "eSIM - ",
  PREOWNED: "SIM/eSIM - ",
  TECHNICAL_PARTNER_DELIVERY: "",
};

export function SidebarMobileOfferDuoTrioNumber({
  currentOnboardingMember,
  discountFeatures,
  isOrderSummary,
  orderItems,
  isPostpaid,
}: ISidebarMobileOfferDuoTrioNumberProps) {
  if (!orderItems || !currentOnboardingMember) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const discount3x0Promo = discountFeatures?.some((item) => {
    return item.name === "nju_mobile_discount_3x0MnpPromotion_HIGHLIGHT";
  });

  return (
    <>
      {orderItems.map((item, index) => {
        const activationFee =
          item.orderSummary?.items?.find((item) => {
            return (
              item.offerId ===
              (isPostpaid
                ? "P-NJU-Activation-Payment-Postpaid"
                : "P-NJU-Activation-Payment")
            );
          })?.offerPrice ?? 0;
        const isActivationVisible =
          (isOrderSummary || isPostpaid) && activationFee > 0;
        return (
          <React.Fragment key={index}>
            {index < currentOnboardingMember && (
              <div data-cy="msisdn-single-offer-panel" key={`element-${index}`}>
                {orderItems[index - 1]?.verifiedMsisdn && (
                  <Separator size="s" thickness="medium" tone="complementary" />
                )}
                {item.verifiedMsisdn && item.processId && (
                  <>
                    <BlockText testId="offer-type" tone="accent">
                      {`${
                        item.deliveryMethodId
                          ? mapDeliveryMethodIdToText[item.deliveryMethodId]
                          : ""
                      }${mapProcessIdToText[item.processId]}`}
                    </BlockText>
                    <BlockText size="xl" weight="bold">
                      <div
                        className="flex items-center"
                        data-cy="msisdn-details"
                      >
                        {formatPhoneNumber(
                          item.verifiedMsisdn,
                          POSITIVE_DIGITS,
                          " ",
                          "",
                          true,
                        )}
                      </div>
                    </BlockText>
                  </>
                )}
                {!item.verifiedMsisdn && (
                  <BlockText size="xl" weight="bold">
                    numer {index + 1} z {orderItems.length}
                  </BlockText>
                )}
                <Separator size="xxs" tone="invisible" />
                {item.orderSummary && (
                  <>
                    {isActivationVisible && (
                      <div className="grid gap-2 grid-cols-[3fr_1fr] items-center mb-1">
                        <Text>jednorazowa opłata aktywacyjna:</Text>
                        <Text align="right">
                          {formatPrice(activationFee)} zł
                        </Text>
                      </div>
                    )}
                    {!!item.orderSummary.totalOrderDiscount && (
                      <div
                        className="grid gap-2 grid-cols-[2fr_1fr] items-center mb-1"
                        data-cy="month-discount"
                      >
                        <Text>rabat miesięczny:</Text>
                        <Text align="right">
                          -{formatPrice(item.orderSummary.totalOrderDiscount)}{" "}
                          zł
                        </Text>
                      </div>
                    )}
                    <div
                      className="grid gap-2 grid-cols-[2fr_1fr] items-center mt-1"
                      data-cy="recurring-price"
                    >
                      <Text weight="bold">opłata miesięczna:</Text>
                      <Text
                        align="right"
                        size="xl"
                        testId="box-payment-price"
                        weight="bold"
                      >
                        {formatPrice(
                          item.orderSummary.totalRecurringOrderPrice,
                        )}{" "}
                        zł
                      </Text>
                    </div>
                    <Separator size="xxs" tone="invisible" />
                  </>
                )}
              </div>
            )}
            {item.processId === "MNP_IN" && discount3x0Promo && (
              <div className="flex gap-[8px]">
                <Icon name="info-circle" tone="accent" />
                <BlockText size="s" tone="accent">
                  przez pierwsze 3 miesiące
                </BlockText>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
